import React from "react";
import {
  TbLayoutDashboard,
  TbTemplate,
  TbReportMedical,
  TbReport,
  TbCalendarTime,
  TbClock,
  TbNotes,
  TbPill,
  TbHome,
  TbDots,
} from "react-icons/tb";
import { LuUsers, LuUserRoundCog, LuBell } from "react-icons/lu";
import { GrSchedules } from "react-icons/gr";
import markLogo from "../../assets/images/no-text-logo.svg";

// Mirrors the real dashboard sidebar (layouts/Sidebar.tsx) so the preview
// shows the product as it is.
const NAV = [
  { label: "Dashboard", icon: TbLayoutDashboard, active: true },
  { label: "Users", icon: LuUsers },
  { label: "Templates", icon: TbTemplate },
  { label: "Rotas", icon: GrSchedules },
  { label: "Medication", icon: TbReportMedical },
  { label: "Reports", icon: TbReport },
  { label: "Admin", icon: LuUserRoundCog },
];

const STATS = [
  { value: 24, label: "Shifts today", tone: "navy" },
  { value: 9, label: "In progress", tone: "amber" },
  { value: 13, label: "Completed", tone: "green" },
  { value: 11, label: "Medication given", tone: "violet" },
  { value: 2, label: "PRN given", tone: "magenta" },
];

const VISITS = [
  { initials: "MJ", name: "Mary Johnson", type: "Personal care", time: "08:00 – 09:00", status: "Completed" },
  { initials: "DS", name: "David Smith", type: "Medication support", time: "10:00 – 11:00", status: "In progress" },
  { initials: "PB", name: "Patricia Brown", type: "Domestic support", time: "12:00 – 13:00", status: "Scheduled" },
  { initials: "JW", name: "James Wilson", type: "Personal care", time: "14:00 – 15:00", status: "Scheduled" },
];

const ACTIVITY = [
  { text: "Care note added", time: "10:24" },
  { text: "Medication administered", time: "09:18" },
  { text: "Clocked in · D. Smith", time: "09:58" },
];

const statusClass = (s) => s.toLowerCase().replace(/\s+/g, "-");

// Donut segments for Completed / In progress / Scheduled (13 / 9 / 2 of 24)
const DONUT = [
  { pct: 54, color: "var(--lp-green)" },
  { pct: 38, color: "var(--lp-amber)" },
  { pct: 8, color: "var(--lp-navy-300)" },
];

function Donut() {
  const r = 30;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 80 80" className="mock-donut">
      <circle cx="40" cy="40" r={r} fill="none" stroke="#EEF0FA" strokeWidth="10" />
      {DONUT.map((seg, i) => {
        const len = (seg.pct / 100) * c;
        const el = (
          <circle
            key={i}
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth="10"
            strokeDasharray={`${len - 1.5} ${c - len + 1.5}`}
            strokeDashoffset={-offset}
            transform="rotate(-90 40 40)"
          />
        );
        offset += len;
        return el;
      })}
      <text x="40" y="44" textAnchor="middle" className="mock-donut__value">
        54%
      </text>
    </svg>
  );
}

export default function HeroMockup() {
  return (
    <div className="hero-mockup" aria-hidden="true">
      {/* Laptop — web dashboard */}
      <div className="mock-laptop">
        <div className="mock-laptop__screen">
          <div className="mock-app">
            <div className="mock-app__topbar">
              <div className="mock-app__brand">
                <img src={markLogo} alt="" />
                <span>CARECONNECT</span>
              </div>
              <div className="mock-app__user">
                <LuBell className="mock-app__bell" />
                <span className="mock-avatar mock-avatar--navy">SA</span>
                <div>
                  <strong>Sarah Adams</strong>
                  <small>Care Manager</small>
                </div>
              </div>
            </div>

            <div className="mock-app__body">
              <nav className="mock-app__nav">
                {NAV.map(({ label, icon: Icon, active }) => (
                  <span key={label} className={active ? "is-active" : ""}>
                    <Icon /> {label}
                  </span>
                ))}
              </nav>

              <div className="mock-app__main">
                <div className="mock-app__greeting">
                  <strong>Good morning, Sarah</strong>
                  <small>Here's what's happening today</small>
                </div>

                <div className="mock-stats">
                  {STATS.map((s) => (
                    <div key={s.label} className={`mock-stat mock-stat--${s.tone}`}>
                      <b>{s.value}</b>
                      <small>{s.label}</small>
                    </div>
                  ))}
                </div>

                <div className="mock-panels">
                  <div className="mock-panel">
                    <div className="mock-panel__head">
                      <strong>Today's shifts</strong>
                      <small>View rota</small>
                    </div>
                    {VISITS.map((v) => (
                      <div key={v.name} className="mock-visit">
                        <span className="mock-avatar">{v.initials}</span>
                        <div className="mock-visit__who">
                          <strong>{v.name}</strong>
                          <small>{v.type}</small>
                        </div>
                        <small className="mock-visit__time">{v.time}</small>
                        <span className={`mock-pill mock-pill--${statusClass(v.status)}`}>
                          {v.status}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mock-panel">
                    <div className="mock-panel__head">
                      <strong>Shift status</strong>
                    </div>
                    <div className="mock-donut-row">
                      <Donut />
                      <ul className="mock-legend">
                        <li><i style={{ background: "var(--lp-green)" }} />Completed</li>
                        <li><i style={{ background: "var(--lp-amber)" }} />In progress</li>
                        <li><i style={{ background: "var(--lp-navy-300)" }} />Scheduled</li>
                      </ul>
                    </div>
                    <div className="mock-panel__head mock-panel__head--sub">
                      <strong>Recent activity</strong>
                    </div>
                    {ACTIVITY.map((a) => (
                      <div key={a.text} className="mock-activity">
                        <span>{a.text}</span>
                        <small>{a.time}</small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mock-laptop__base" />
      </div>

      {/* Phone — field carer app */}
      <div className="mock-phone">
        <div className="mock-phone__notch" />
        <div className="mock-phone__screen">
          <div className="mock-phone__header">
            <img src={markLogo} alt="" />
            <small>CARECONNECT</small>
            <strong>Good morning, Emma</strong>
          </div>
          <div className="mock-phone__shift">
            <small>Next shift · 12:00</small>
            <strong>Patricia Brown</strong>
            <span className="mock-phone__clock">
              <TbClock /> Clock in
            </span>
          </div>
          <div className="mock-phone__grid">
            <span><TbCalendarTime /> My shifts</span>
            <span><TbNotes /> Care notes</span>
            <span><TbPill /> Medication</span>
            <span><TbPill /> PRN</span>
          </div>
          <div className="mock-phone__tabbar">
            <span className="is-active"><TbHome /></span>
            <span><TbCalendarTime /></span>
            <span><TbDots /></span>
          </div>
        </div>
      </div>
    </div>
  );
}
